// Property field-mapping seam.
//
// toPropertyOverview is verified against a real POST /vehicle response (see
// this file's git history / PR description for the captured sample) — every
// field below has a confirmed source. /assessment's shape is still
// unconfirmed, so it's treated as a fallback source only, and every access
// stays optional-chained so a missing/renamed field never crashes the UI.
//
// toPropertyListRow/toPropertyList are verified against a real POST
// /assessment_list/all response: rows carry { address, car_images,
// claim_state, country_corp_id, created_at, id, ref_num }, wrapped in a
// { Status, data, next, previous, total_countOfClaims } envelope.

const absoluteOrNull = (url) =>
  typeof url === "string" && /^https?:\/\//.test(url) ? url : null;

// One slash between the host and the path.
//
// This used to emit the double slash "https://…camcom.ai//home/camcom/…" on
// the stated grounds that the host 404s the single-slash form. Probed
// directly, both forms return the same 220561-byte JPEG, so the double slash
// was neither load-bearing nor harmful — just surprising in a URL bar and in
// a bug report. picture is an absolute filesystem-style path
// ("/home/camcom/media_staging/…") and mediaBaseUrl never has a trailing
// slash, so trimming one side is enough.
export const buildPropertyImageUrl = (mediaBaseUrl, picture) => {
  if (!mediaBaseUrl || !picture) return null;
  return `${String(mediaBaseUrl).replace(/\/+$/, "")}/${String(picture).replace(/^\/+/, "")}`;
};

export const toPropertyListRow = (raw) => ({
  ref_num: raw?.ref_num ?? "",
  id: raw?.id ?? null,
  status: raw?.claim_state ?? raw?.status ?? null,
  country: raw?.country_corp_id ?? raw?.countryname ?? raw?.country ?? "",
  address: raw?.address ?? "",
  createdAt: raw?.created_at ?? raw?.timestamp ?? null,
  // No property equivalent of a vehicle's num_plate — ref_num is the id.
  displayId: raw?.ref_num ?? "",
  // car_images comes back as {} for property, so rows have no thumbnail; the
  // fallbacks stay only so a backend that later populates one is picked up.
  thumbnailUrl: absoluteOrNull(raw?.picture ?? raw?.thumbnail ?? raw?.image),
});

/**
 * Unpacks the /assessment_list/all envelope: `data` is an object keyed by
 * stringified index rather than an array, `total_countOfClaims` is the figure
 * behind the "Total Inspections" tile (there is no separate
 * /assessment_report call in the property flow), and `next.offset` drives
 * paging — it is absent/null on the last page.
 */
export const toPropertyList = (response) => {
  const rawRows = response?.data;
  const rows = rawRows && typeof rawRows === "object" ? Object.values(rawRows) : [];
  const nextOffset = Number(response?.next?.offset);
  const total = Number(response?.total_countOfClaims);
  return {
    rows: rows.map(toPropertyListRow),
    total: Number.isFinite(total) ? total : null,
    nextOffset: Number.isFinite(nextOffset) ? nextOffset : null,
  };
};

// property_data[].side is one of "exterior" | "interior" | "additional" in
// the confirmed sample — bucket by that value directly rather than guessing
// at keywords the way Motor's IDENTIFIER_KEYWORDS/EXTERIOR_SIDES do, since
// property's API already tells us the category. Anything that isn't
// recognized joins "additional" (the API's own catch-all category) rather
// than a synthetic 4th bucket, so exterior+interior+additional always sums
// to images.length exactly — the vehicle payload's own exterior_count/
// interior_count/additional_count fields have been observed to NOT sum to
// image_count on real data, so counts shown in the UI are derived from this
// grouping, not from those fields.
/**
 * Photographs split into the three cards the page renders.
 *
 * The match is normalised rather than exact. It used to be `=== "exterior"`,
 * so "Exterior", " exterior" or "EXTERIOR" all fell through to `additional`
 * — and because the page drops a card whose bucket is empty, the Exterior
 * card at the top of the page simply did not render. Nothing looked broken;
 * the photographs were in the page, under the wrong heading.
 *
 * Unknown or missing sides still land in `additional`, which is the honest
 * place for them: it says "we have this photograph but not where it belongs"
 * rather than guessing.
 */
const sideKey = (side) =>
  typeof side === "string" ? side.trim().toLowerCase() : "";

export const groupPropertyImagesBySide = (images) => {
  const buckets = { exterior: [], interior: [], additional: [] };
  (Array.isArray(images) ? images : []).forEach((img) => {
    const side = sideKey(img?.side);
    if (side === "exterior") buckets.exterior.push(img);
    else if (side === "interior") buckets.interior.push(img);
    else buckets.additional.push(img);
  });
  return buckets;
};

// The AI summary answers a field even when the photos didn't actually show
// it — "not shown", "no visible X", "unclear", "n/a" — rather than leaving it
// blank. A truthy string is therefore not enough to mean "this was observed":
// callers that show a checkmark or a value for "the photos covered this"
// need to treat these the same as absent, not as a real answer.
const UNOBSERVED_PATTERN =
  /^(not[ _]?(shown|visible|assessed|available|applicable|observed)|no visible|unclear|unknown|n\/?a\b|none)\b/i;

export const isUnobservedValue = (value) =>
  typeof value === "string" && UNOBSERVED_PATTERN.test(value.trim());

/**
 * Client-side guarantee for the country filter on the property case list.
 *
 * The list also asks the server to narrow by country via the
 * /assessment_list/all `countryname` param, but that depends on matching
 * whatever casing/format the property API compares against internally —
 * unconfirmed, since country isn't a field this app owns. Real captured rows
 * spell it "Estonia"/"Lithuania" (Title Case, full name — see
 * propertyAssessment.test.js), which is what the caller now sends, but this
 * filters again here regardless, against each row's own already-normalised
 * `country` (see toPropertyListRow below), so a country picked on the list
 * shows only that country's rows no matter what the server actually did with
 * the param.
 */
export const filterRowsByCountry = (rows, selectedCountry) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!selectedCountry || selectedCountry === "All") return list;
  const target = selectedCountry.toLowerCase().trim();
  return list.filter((row) => row?.country?.toLowerCase().trim() === target);
};

/**
 * Resolves a Property Insights result's evidence_images/image_inventory
 * (1-based indices) against the `images` list usePropertyAiSummary tracked
 * for the request that produced it.
 *
 * Keyed by each image's `index` field rather than its array position so a
 * caller can always pass the same list this module built via
 * toSummaryImageIndex below, regardless of how it's filtered/reordered
 * elsewhere. The index itself is this app's own doing now — see that
 * function — not something the service hands back.
 */
export const resolveSummaryEvidenceUrls = (indices, images, mediaBaseUrl) => {
  const byIndex = new Map(
    (Array.isArray(images) ? images : []).map((img) => [img?.index, img]),
  );
  return (Array.isArray(indices) ? indices : [])
    .map((i) => buildPropertyImageUrl(mediaBaseUrl, byIndex.get(i)?.picture))
    .filter(Boolean);
};

/**
 * The same {index, picture} shape resolveSummaryEvidenceUrls/
 * ImageInventoryStrip read, built from whatever photos are about to be
 * submitted to Property Insights — in submission order, since that order is
 * exactly what evidence_images/image_inventory's indices mean per the
 * service's own contract ("1-based indexes into the images in upload
 * order"). Entries with no `picture` are dropped before indexing, so the
 * sequence here matches the files actually uploaded one-to-one.
 */
export const toSummaryImageIndex = (photos) =>
  (Array.isArray(photos) ? photos : [])
    .filter((photo) => photo?.picture)
    .map((photo, i) => ({ index: i + 1, picture: photo.picture }));

export const toPropertyOverview = (assessmentRaw, vehicleRaw) => {
  // /vehicle is the confirmed-shape source; /assessment (shape unconfirmed)
  // only fills in anything /vehicle happens to omit.
  const v = vehicleRaw || {};
  const a = assessmentRaw || {};
  const link = v.link_generated_details || a.link_generated_details || {};

  // property_data carries gallery_flag and lat/long per photo — the only
  // place either signal exists, since neither /vehicle nor /assessment states
  // "uploaded from gallery" or "no location permission" directly. A photo
  // the device didn't tag with coordinates comes back with the literal
  // string "None" for both lat and long, not an absent field or null.
  const propertyImages = v.property_data ?? a.property_data ?? [];
  const hasGalleryUpload = propertyImages.some(
    (img) => Number(img?.gallery_flag) === 1,
  );
  const hasAnyLocation = propertyImages.some(
    (img) => Number.isFinite(parseFloat(img?.lat)) && Number.isFinite(parseFloat(img?.long)),
  );
  const noLocationPermission = propertyImages.length > 0 && !hasAnyLocation;

  const warnings = [
    hasGalleryUpload
      ? "This inspection has images uploaded from the gallery."
      : null,
    noLocationPermission ? "User did not provide location permission." : null,
    v.low_internet_speed || a.low_internet_speed || null,
  ]
    .filter(Boolean)
    // dedupe defensively — the same warning could plausibly appear on both
    // sides if a future /assessment response echoes it back too.
    .filter((msg, i, arr) => arr.indexOf(msg) === i);

  return {
    refNum: v.ref_num ?? a.ref_num ?? "",
    status: v.claim_state ?? a.claim_state ?? null,
    address: v.address ?? a.address ?? "",
    createdAt: v.created_at ?? a.created_at ?? null,
    updatedAt: v.updated_at ?? a.updated_at ?? null,

    ergoId: link.ergoid ?? "",
    inspectionType: link.inspection_type ?? "",
    propertyType: link.property_type ?? "",
    country:
      link.country ??
      v.country_corp_id ??
      a.country_corp_id ??
      v.country ??
      a.country ??
      "",

    timeTakenToUpload: v.time_taken_to_upload ?? "",
    deviceMakeModel: v.device_make_model ?? "",
    osSummary: v.os_summary ?? "",
    inspectionMode: v.inspection_mode ?? "",
    minInternetSpeed: v.min_internet_speed ?? null,
    maxInternetSpeed: v.max_internet_speed ?? null,

    warnings,

    userRating: v.user_rating ?? null,
    reviewerComment: v.reviewer_comment ?? v.comment ?? v.trinetra_user_comment ?? "",
    uploadedBy: v.user_id ?? "",

    documents: Array.isArray(v.documents) ? v.documents : [],
    images: Array.isArray(v.property_data) ? v.property_data : [],

    // Nothing observed is silently dropped, so a future field-mapping fix
    // has the raw payloads on hand without needing to re-fetch.
    _raw: { assessment: assessmentRaw, vehicle: vehicleRaw },
  };
};
